/**
 * One group on My Support, collapsible or fixed.
 *
 * The trigger is the whole heading row rather than a chevron on its own: a
 * 16px target beside a 200px heading is a worse click for everyone and a much
 * worse one on a touch screen. `Collapsible.Trigger` renders a real `<button>`,
 * so Enter, Space and focus order arrive already correct from the design
 * system — and it appends its own chevron after the children, which is why
 * there is none written here. Adding one put two on every row.
 *
 * `collapsible={false}` is for "Waiting on Me". Anything that needs the
 * employee to act should not be one click away from being hidden, and a group
 * someone can fold shut and forget is the wrong home for it — so that section
 * renders as a plain heading with no toggle and no chevron at all, rather than
 * as a collapsible that happens to start open.
 *
 * The count stays in the heading deliberately. A collapsed section that says
 * only "Tickets & Requests" makes the employee open it to discover whether it
 * was worth opening; one that shows a count has already answered that.
 */

import { Icon, type IconComponent } from "@norican/icons";
import { Badge, Collapsible, Typography } from "@norican/react";
import { type ReactNode, useState } from "react";

export interface SupportSectionProps {
  /** Pinned on a first visit. Only "Waiting on Me" has any business being so. */
  defaultOpen: boolean;
  /** False pins the section open and removes the toggle entirely. */
  collapsible?: boolean;
  label: string;
  /** Shown beside the label, and the reason a collapsed section is still useful. */
  count?: number;
  icon?: IconComponent;
  children: ReactNode;
}

/** Heading row content, shared so the fixed and collapsible forms cannot drift. */
function SectionHeading({
  label,
  count,
  icon,
}: Pick<SupportSectionProps, "label" | "count" | "icon">) {
  return (
    <>
      {icon === undefined ? null : <Icon icon={icon} size="sm" tone="accent" />}
      <Typography as="span" variant="heading-5" className="min-w-0">
        {label}
      </Typography>
      {count === undefined || count === 0 ? null : (
        <Badge tone="neutral" variant="outline" size="sm">
          {count}
        </Badge>
      )}
    </>
  );
}

export function SupportSection({
  defaultOpen,
  collapsible = true,
  label,
  count,
  icon,
  children,
}: SupportSectionProps) {
  /* Always collapsed on arrival. Nothing is remembered between visits: the
     sidebar is a glance, and a group someone opened days ago still being open
     is noise rather than a preference. "Waiting on Me" is the exception and
     does not come through here at all — it is `collapsible={false}`. */
  const [open, setOpen] = useState(defaultOpen);

  /* Both forms share one wrapper so they cannot look different.

     They used to diverge: the fixed section rendered a bare heading over its
     content while the collapsible one sat in the design system's own
     `Collapsible.Root`, so "Waiting on Me" read as a loose label on the page
     while every other group read as a panel. Same surface, same padding, same
     heading row for both now — the only difference is whether that row is a
     button, which is the only difference that was ever meant to exist. */
  const heading = <SectionHeading label={label} count={count} icon={icon} />;
  const surface = "border-border bg-surface flex flex-col gap-3 rounded-lg border px-3 py-2";

  if (!collapsible) {
    return (
      <section className={surface} aria-label={label}>
        <div className="flex min-w-0 items-center gap-2">{heading}</div>
        {children}
      </section>
    );
  }

  return (
    <Collapsible.Root
      open={open}
      onOpenChange={setOpen}
      className={surface}
      render={<section aria-label={label} />}
    >
      <Collapsible.Trigger className="group hover:text-accent-fg-subtle ease-standard rounded-md transition-colors">
        {/* One wrapper, not three loose children. The design system's trigger
            lays its children out with `justify-content: space-between`, so
            passing the icon, the heading and the badge separately spread them
            across the full width and left the title floating mid-row. */}
        <span className="flex min-w-0 flex-1 items-center gap-2">{heading}</span>
      </Collapsible.Trigger>

      <Collapsible.Panel>{children}</Collapsible.Panel>
    </Collapsible.Root>
  );
}
